import React, { useRef } from "react";
import { createPortal } from "react-dom";
import { Box, Typography, Button, useTheme, Paper } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import QrCode2OutlinedIcon from "@mui/icons-material/QrCode2Outlined";
import { QRCodeSVG } from "qrcode.react";
import { useReactToPrint } from "react-to-print";
import {
  BRAND_COLOR,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import { alpha } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { BatchCreateResult } from "./types/productItemWizard";
import GuidanceDialogShell from "../common/GuidanceDialog/GuidanceDialogShell";
import {
  guidanceOutlinedButtonSx,
  guidancePrimaryButtonSx
} from "../common/GuidanceDialog/guidanceDialogStyles";

export interface ProductItemBatchResultViewProps {
  open: boolean;
  onClose: () => void;
  onAddMore: () => void;
  productName?: string;
  batchResult: BatchCreateResult;
  /** Distinguishes clone flow vs new stock wizard for button copy. */
  flow?: "wizard" | "clone";
}

const ProductItemBatchResultView: React.FC<ProductItemBatchResultViewProps> = ({
  open,
  onClose,
  onAddMore,
  productName = "",
  batchResult,
  flow = "wizard"
}) => {
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useKivunovaTranslation();

  const printContentRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
    contentRef: printContentRef,
    documentTitle: t("add-items-in-stock-qr-codes-print-title", "QR Codes")
  });

  const createdItemIds = batchResult.createdIds;
  const itemCount = createdItemIds.length;
  const itemLabel =
    itemCount === 1
      ? t("batch-result-one-label-created", "1 label created")
      : t("batch-result-labels-created", "{{count}} labels created", {
          count: itemCount
        });

  const addMoreLabel =
    flow === "clone"
      ? t("clone-stock-add-more-like-this", "Clone this item again")
      : t("batch-result-receive-more-stock", "Receive more stock");

  const printableContent = (
    <div
      style={{
        position: "fixed",
        left: "-9999px",
        top: 0,
        zIndex: -1
      }}
      aria-hidden="true"
    >
      <div
        ref={printContentRef}
        style={{
          width: "100%",
          maxWidth: 1000,
          background: "white",
          padding: 16,
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 0
        }}
      >
        {createdItemIds.map((itemId) => (
          <div
            key={itemId}
            style={{
              padding: 20,
              textAlign: "center",
              pageBreakInside: "avoid",
              breakInside: "avoid"
            }}
          >
            <QRCodeSVG value={itemId} size={150} />
            <div
              style={{
                marginTop: 8,
                fontSize: 12,
                fontWeight: 600,
                wordBreak: "break-all"
              }}
            >
              {itemId}
            </div>
            {productName && (
              <div
                style={{
                  marginTop: 4,
                  fontSize: 11,
                  color: "#666"
                }}
              >
                {productName}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <>
      {createPortal(printableContent, document.body)}
      <GuidanceDialogShell
        open={open}
        onClose={onClose}
        title={t("batch-result-labels-ready-title", "Labels ready to print")}
        subtitle={
          batchResult.failedCount === 0
            ? `${itemLabel}${productName ? ` ${t("add-items-in-stock-for", "for")} ${productName}` : ""}`
            : `${itemCount} ${t("add-items-in-stock-created", "created")}, ${batchResult.failedCount} ${t("add-items-in-stock-failed", "failed")}`
        }
        icon={<QrCode2OutlinedIcon sx={{ fontSize: 20 }} />}
        maxWidth={1200}
        dataTestId="product-item-batch-result"
        headerActions={
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            size="small"
            sx={{
              ...guidancePrimaryButtonSx,
              py: 0.5,
              px: 1.5,
              fontSize: "0.75rem"
            }}
          >
            {t("batch-result-print-labels", "Print labels")}
          </Button>
        }
        footer={
          <>
            <Button
              onClick={onAddMore}
              variant="outlined"
              size="medium"
              data-testid="batch-result-add-more"
              sx={{
                ...guidanceOutlinedButtonSx,
                minWidth: matchMD ? "100%" : 160
              }}
            >
              {addMoreLabel}
            </Button>
            <Button
              onClick={onClose}
              variant="contained"
              size="medium"
              data-testid="batch-result-done"
              sx={{
                ...guidancePrimaryButtonSx,
                minWidth: matchMD ? "100%" : 120
              }}
            >
              {t("add-items-in-stock-done", "Done")}
            </Button>
          </>
        }
      >
        <Typography
          variant="body2"
          color="text.secondary"
          mb={2}
          sx={{ fontSize: "0.8125rem" }}
        >
          {t(
            "batch-result-print-hint",
            "Stick each label on one container or package so staff can scan it at checkout."
          )}
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)",
              sm: "repeat(3, 1fr)",
              md: "repeat(4, 1fr)",
              lg: "repeat(5, 1fr)"
            },
            gap: 2
          }}
        >
          {createdItemIds.map((itemId) => (
            <Paper
              key={itemId}
              elevation={0}
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 200,
                borderRadius: 2,
                border: `1px solid ${alpha(BRAND_COLOR, 0.1)}`,
                bgcolor: alpha(BRAND_COLOR, 0.02)
              }}
            >
              <QRCodeSVG value={itemId} size={150} />
              <Typography
                variant="body2"
                sx={{
                  mt: 2,
                  fontWeight: 600,
                  textAlign: "center",
                  wordBreak: "break-all"
                }}
              >
                {itemId}
              </Typography>
              {productName ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, textAlign: "center" }}
                >
                  {productName}
                </Typography>
              ) : null}
            </Paper>
          ))}
        </Box>
      </GuidanceDialogShell>
    </>
  );
};

export default ProductItemBatchResultView;
